// Thin, typed fetch client over Core's public + admin API
// (docs/architecture/08-contracts.md #1, #2), built on the types generated
// from schemas/openapi/core.yaml by `make ui-generate` (openapi-typescript,
// see ./schema.ts). Auth is the session cookie only (10-security.md #5);
// requests always send `credentials: "include"` and never carry a service
// or admin token -- those stay in Core (08-contracts.md #2's admin proxy).
import { API_BASE } from "./config";
import type { components } from "./schema";

export type TaskView = components["schemas"]["TaskView"];
export type ResultSlot = components["schemas"]["ResultSlot"];
export type Alias = components["schemas"]["Alias"];
export type Account = components["schemas"]["Account"];
export type Quote = components["schemas"]["Quote"];
export type Catalog = components["schemas"]["Catalog"];
export type AliasAvailability = components["schemas"]["AliasAvailability"];
export type CatalogTarget = components["schemas"]["CatalogTarget"];
export type ArtifactAllocation = components["schemas"]["ArtifactAllocation"];
export type ArtifactView = components["schemas"]["ArtifactView"];
export type ApiKey = components["schemas"]["ApiKey"];
export type ApiKeyCreated = components["schemas"]["ApiKeyCreated"];
export type UsageRecord = components["schemas"]["UsageRecord"];
export type AdminUser = components["schemas"]["User"];
export type AdminSummary = components["schemas"]["AdminSummary"];
export type LedgerTotals = components["schemas"]["LedgerTotals"];
export type StorageView = components["schemas"]["StorageView"];
export type StorageReconcileReport = components["schemas"]["StorageReconcileReport"];

// AdminJobView / AdminAttemptView / AdminTaskView are not (yet) named
// components in core.yaml (they're documented only as "full view incl.
// jobs/attempts" prose, internal/core/admin.go #219); declared by hand
// here to match internal/core/admin.go's AdminTaskView/AdminJobView/
// AdminAttemptView exactly.
export interface AdminAttemptView {
  attempt_id: string;
  runtime: string;
  deployment: string;
  state: string;
  error?: { code: string; message: string; details?: unknown } | null;
  accepted_at: string;
  started_at?: string | null;
  finished_at?: string | null;
  /** The worker's whole output report -- artifact refs plus every
   * measurement scalar (admin-only; see lib/attemptMeta.ts). */
  outputs?: Record<string, unknown> | null;
}
export interface AdminJobView {
  job_id: string;
  step: string;
  job_definition: string;
  state: string;
  coordinates: Record<string, number>;
  excluded_runtimes: string[];
  free_attempts: number;
  paid_attempts: number;
  attempts: AdminAttemptView[];
}
export interface AdminTaskView extends TaskView {
  user_id: string;
  jobs: AdminJobView[];
}

export class ApiError extends Error {
  status: number;
  code: string;
  details?: unknown;
  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

type Unauthorized = () => void;
let unauthorizedHandler: Unauthorized | null = null;
/** Registered once by AuthProvider so any 401 anywhere sends the user to /login. */
export function onUnauthorized(handler: Unauthorized): void {
  unauthorizedHandler = handler;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const resp = await fetch(API_BASE + path, {
    ...init,
    credentials: "include",
    headers: { ...(init?.body ? { "Content-Type": "application/json" } : {}), ...(init?.headers ?? {}) },
  });
  if (resp.status === 401) {
    unauthorizedHandler?.();
    throw new ApiError(401, "UNAUTHENTICATED", "not signed in");
  }
  if (resp.status === 204) {
    return undefined as T;
  }
  const text = await resp.text();
  const body = text ? JSON.parse(text) : undefined;
  if (!resp.ok) {
    const err = body?.error ?? { code: "UNKNOWN", message: resp.statusText };
    throw new ApiError(resp.status, err.code, err.message, err.details);
  }
  return body as T;
}

function get<T>(path: string): Promise<T> {
  return request<T>(path);
}
function post<T>(path: string, body?: unknown): Promise<T> {
  return request<T>(path, { method: "POST", body: body !== undefined ? JSON.stringify(body) : undefined });
}
function del<T>(path: string): Promise<T> {
  return request<T>(path, { method: "DELETE" });
}
function put<T>(path: string, body?: unknown): Promise<T> {
  return request<T>(path, { method: "PUT", body: body !== undefined ? JSON.stringify(body) : undefined });
}

// ---- Auth -------------------------------------------------------------
export function login(email: string, password: string) {
  return post<{ user_id: string; email: string; role: string }>("/auth/login", { email, password });
}
export function logout() {
  return post<void>("/auth/logout");
}

// ---- Account ------------------------------------------------------------
export function getAccount() {
  return get<Account>("/account");
}
export function getAccountUsage(params?: { from?: string; to?: string }) {
  const q = toQuery(params);
  return get<UsageRecord[]>("/account/usage" + q);
}
export function listApiKeys() {
  return get<ApiKey[]>("/account/api-keys");
}
export function createApiKey(label?: string) {
  return post<ApiKeyCreated>("/account/api-keys", { label });
}
export function deleteApiKey(id: string) {
  return del<void>("/account/api-keys/" + id);
}

// ADR-0047: the feature flags and the offered targets with their prices.
export function getCatalog() {
  return get<Catalog>("/catalog");
}
// ADR-0048: is a worker for each alias up, busy, starting or asleep.
export function getAvailability() {
  return get<{ aliases: AliasAvailability[] }>("/availability");
}

// ---- Aliases / quote ------------------------------------------------------
export function listAliases() {
  return get<Alias[]>("/aliases");
}
export function getAlias(name: string) {
  return get<Alias>("/aliases/" + encodeURIComponent(name));
}
// seconds: each result's length, priced by the alias's per_second rate
// (ADR-0041); omitted for an alias that takes no length. fps with seconds
// prices a Catalog target (ADR-0047).
export function quoteAlias(name: string, results: number, seconds?: number, fps?: number) {
  const len = (seconds ? `&seconds=${seconds}` : "") + (fps ? `&fps=${fps}` : "");
  return get<Quote>(`/aliases/${encodeURIComponent(name)}/quote?results=${results}${len}`);
}

// ---- Artifacts --------------------------------------------------------
/** label (1-200 chars) and attrs (flat, ≤ 32 keys) are optional (08-contracts.md §1). */
export function allocateArtifact(
  contentType: string,
  size?: number,
  meta?: { label?: string; attrs?: Record<string, string | number | boolean> },
) {
  return post<ArtifactAllocation>("/artifacts", { content_type: contentType, size, ...meta });
}
export function getArtifact(id: string) {
  return get<ArtifactView>("/artifacts/" + id);
}
export function deleteArtifact(id: string) {
  return del<void>("/artifacts/" + id);
}

export type ArtifactKind = "INPUT" | "OUTPUT" | "INTERMEDIATE";

/** Server-derived facts about an artifact's bytes (ADR-0021,
 * docs/architecture/05-store.md Â§7): dimensions first, then duration/
 * codecs for video and audio. Read-only and entirely separate from
 * user-set `attrs` -- never present until Store (or the admin backfill
 * route) has probed the artifact. A field a format does not provide is
 * simply absent, never zero/false; `kind` is the only field always
 * present, `{kind: "other"}` for an unrecognized or malformed format. */
export interface MediaInfo {
  kind: "image" | "video" | "audio" | "other";
  format?: string;
  mime?: string;
  error?: string;
  content_type_mismatch?: boolean;
  width?: number;
  height?: number;
  orientation?: number;
  bit_depth?: number;
  has_alpha?: boolean;
  animated?: boolean;
  frames?: number;
  color_space?: string;
  rotation?: number;
  duration_s?: number;
  fps?: number;
  vcodec?: string;
  has_audio?: boolean;
  faststart?: boolean;
  acodec?: string;
  sample_rate?: number;
  channels?: number;
}

// GET /artifacts (the Library list) and the extra fields its items carry
// -- kind, label, attrs, media, task_id, created_at, committed_at -- are
// not yet in schemas/openapi/core.yaml: Core's side of this contract is
// being built in parallel (docs/plan/phase-10-ui.md, "Library"). Declared
// by hand here, matching the documented contract exactly, the same way
// AdminTaskView is declared by hand above; once `make ui-generate` picks
// up the real schema this can fold into components["schemas"] instead.
export interface ArtifactListItem extends Omit<ArtifactView, "media" | "attrs"> {
  kind: ArtifactKind;
  label?: string;
  /** Flat string/number/boolean values, always present (an empty object
   * for an artifact with no attrs). Common keys: role ("source" |
   * "result"), trial, model, precision, gpu, provider, machine, seconds,
   * seed, frames, prompt. */
  attrs: Record<string, string | number | boolean>;
  media?: MediaInfo;
  task_id?: string;
  created_at: string;
  committed_at?: string;
}

export interface ListArtifactsResponse {
  items: ArtifactListItem[];
  next_cursor?: string;
}

/** The caller's own COMMITTED artifacts, newest first. `limit` is 1-200;
 * the Library uses 60. `q` matches a substring of `label`; `trial` is an
 * exact match on `attrs.trial`; `content_type_prefix` is e.g. "image/". */
export function listArtifacts(params?: {
  limit?: number;
  cursor?: string;
  kind?: ArtifactKind;
  content_type_prefix?: string;
  q?: string;
  trial?: string;
}) {
  return get<ListArtifactsResponse>("/artifacts" + toQuery(params));
}

// ---- Tasks --------------------------------------------------------------
export interface CreateTaskRequest {
  task_id: string;
  alias: string;
  inputs: Record<string, unknown>;
  scheduling_class?: "realtime" | "standard" | "batch";
}
export function createTask(req: CreateTaskRequest) {
  return post<TaskView>("/tasks", req);
}
export function listTasks(params?: { state?: string; limit?: number; cursor?: string }) {
  return get<TaskView[]>("/tasks" + toQuery(params));
}
export function getTask(id: string) {
  return get<TaskView>("/tasks/" + id);
}
export function cancelTask(id: string) {
  return post<void>(`/tasks/${id}/cancel`);
}
// ADR-0030: "I am about to submit this alias" â lets Policy bring a worker
// up before the Task exists. Best effort; a failure changes nothing for
// the user.
export interface WarmResponse {
  alias: string;
  job_definitions: string[];
  expires_at: string;
}
export function warm(alias: string) {
  return post<WarmResponse>("/warm", { alias });
}

// ---- Admin ----------------------------------------------------------------
export function adminListTasks(params?: { state?: string; limit?: number }) {
  return get<TaskView[]>("/admin/tasks" + toQuery(params));
}
export function adminGetTask(id: string) {
  return get<AdminTaskView>("/admin/tasks/" + id);
}
export function adminListUsers() {
  return get<AdminUser[]>("/admin/users");
}
export function adminCreateUser(email: string, password: string, role: "USER" | "ADMIN") {
  return post<AdminUser>("/admin/users", { email, password, role });
}
export function adminUpdateUser(id: string, patch: { role?: string; disabled?: boolean; password?: string }) {
  return request<AdminUser>("/admin/users/" + id, { method: "PATCH", body: JSON.stringify(patch) });
}
export function adminAdjustBalance(id: string, amountMinor: number, reason: string, source: string, sourceRef: string) {
  return post<{ balance_minor: number }>(`/admin/users/${id}/balance`, {
    amount_minor: amountMinor, reason, source, source_ref: sourceRef,
  });
}
export function adminListAliases() {
  return get<Alias[]>("/admin/aliases");
}
export function adminSetAlias(name: string, preset: string) {
  return put<{ ok: boolean }>("/admin/aliases/" + encodeURIComponent(name), { preset });
}
export function adminUsage(params?: { user?: string; from?: string; to?: string }) {
  return get<UsageRecord[]>("/admin/usage" + toQuery(params));
}
export function adminReloadDefinitions() {
  return post<{ ok: boolean }>("/admin/definitions/reload");
}
export function adminSummary() {
  return get<AdminSummary>("/admin/summary");
}
export function adminStorage() {
  return get<StorageView>("/admin/storage");
}
export function adminStorageReconcile() {
  return post<StorageReconcileReport>("/admin/storage/reconcile");
}

// ---- Admin proxy (08-contracts.md #2: Compute / KillSwitch / Policy) ------
export function proxyCompute<T>(path: string, init?: RequestInit) {
  return request<T>("/admin/proxy/compute/" + path, init);
}
export function proxyKillswitch<T>(path: string, init?: RequestInit) {
  return request<T>("/admin/proxy/killswitch/" + path, init);
}
export function proxyPolicy<T>(path: string) {
  return request<T>("/admin/proxy/policy/" + path);
}

function toQuery(params?: Record<string, string | number | undefined>): string {
  if (!params) return "";
  const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== "");
  if (entries.length === 0) return "";
  const usp = new URLSearchParams();
  for (const [k, v] of entries) usp.set(k, String(v));
  return "?" + usp.toString();
}
